import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MaterialTable, { MaterialTableProps } from 'material-table';
import Moment from 'moment';
import DocumentTypeEntity from '../../../entities/DocumentType.Entity';
import ReferralDocumentEntity from '../../../entities/ReferralDocument.Entity';
import styles from './AttachDocumentsSubmitModal.Styles';

interface IAttachDocumentsSubmitModalProps {
    open: boolean;
    loading: boolean;
    documentTypes: Array<DocumentTypeEntity>;
    availableDocuments: Array<ReferralDocumentEntity>;
    onClose: () => void;
    onNoSubmit: () => void;
    onLoadExpandedView: () => void;
    onSubmitWithDocuments: (documents: Array<ReferralDocumentEntity>) => void;
}

const TableContainer: React.FunctionComponent = ({ children }) => <>{children}</>;

export const AttachDocumentsSubmitModal: FunctionComponent<IAttachDocumentsSubmitModalProps> = (
    props: IAttachDocumentsSubmitModalProps,
) => {
    const classes = styles();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState<Array<number>>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<Array<ReferralDocumentEntity>>([]);

    useEffect(() => {
        if (!props.open) {
            setIsExpanded(false);
            setSelectedDocumentTypeIds([]);
            setSelectedDocuments([]);
        }
    }, [props.open]);

    const orderedDocumentTypes = useMemo(
        () =>
            [...(props.documentTypes ?? [])].sort(
                (a, b) => (a.orderToDisplay ?? Number.MAX_SAFE_INTEGER) - (b.orderToDisplay ?? Number.MAX_SAFE_INTEGER),
            ),
        [props.documentTypes],
    );

    const getIsRowEnabled = (row: ReferralDocumentEntity): boolean =>
        selectedDocumentTypeIds.length > 0 && selectedDocumentTypeIds.includes(row.documentTypeId);

    const selectedDocumentNumberSet = useMemo(
        () => new Set((selectedDocuments ?? []).map((d) => d.documentNumber)),
        [selectedDocuments],
    );

    const tableData = useMemo(
        () =>
            (props.availableDocuments ?? []).map((doc) => {
                const isEnabled = getIsRowEnabled(doc);
                return {
                    ...doc,
                    tableData: {
                        ...(doc as any).tableData,
                        checked: isEnabled && selectedDocumentNumberSet.has(doc.documentNumber),
                        disabled: !isEnabled,
                    },
                };
            }),
        [props.availableDocuments, selectedDocumentNumberSet, selectedDocumentTypeIds],
    );

    const resetExpandedState = () => {
        setSelectedDocumentTypeIds([]);
        setSelectedDocuments([]);
    };

    const handleClose = () => {
        setIsExpanded(false);
        resetExpandedState();
        props.onClose();
    };

    const handleNoSubmit = () => {
        setIsExpanded(false);
        resetExpandedState();
        props.onNoSubmit();
    };

    const handleYes = () => {
        setIsExpanded(true);
        resetExpandedState();
        props.onLoadExpandedView();
    };

    const handleBack = () => {
        setIsExpanded(false);
        resetExpandedState();
    };

    const handleSelectAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const allIds = checked ? orderedDocumentTypes.map((type) => type.documentTypeId) : [];
        setSelectedDocumentTypeIds(allIds);
        if (!checked) {
            setSelectedDocuments([]);
        }
    };

    const handleSelectDocumentType = (documentTypeId: number, checked: boolean) => {
        const updatedIds = checked
            ? [...selectedDocumentTypeIds, documentTypeId]
            : selectedDocumentTypeIds.filter((id) => id !== documentTypeId);

        setSelectedDocumentTypeIds(updatedIds);

        if (updatedIds.length === 0) {
            setSelectedDocuments([]);
            return;
        }

        const filterDocuments = (previousSelected: Array<ReferralDocumentEntity>) =>
            previousSelected.filter((document) => updatedIds.includes(document.documentTypeId));

        setSelectedDocuments(filterDocuments);
    };

    const tableProps: MaterialTableProps<ReferralDocumentEntity> = {
        columns: [
            { title: 'Description', field: 'fileDescription' },
            { title: 'File Name', field: 'fileName' },
            { title: 'Document Type', field: 'documentType' },
            {
                title: 'Date',
                render: (rowData) => (rowData.dueDate ? Moment(rowData.dueDate).format('M/D/YYYY') : ''),
            },
        ],
        data: tableData,
        options: {
            selection: true,
            paging: false,
            toolbar: false,
            maxBodyHeight: 220,
            minBodyHeight: 220,
            headerStyle: {
                paddingTop: 0,
                paddingBottom: 0,
                height: 16,
                minHeight: 16,
                fontSize: '13px',
                lineHeight: '14px',
            },
            selectionProps: (rowData) => ({
                disabled: !getIsRowEnabled(rowData),
                color: 'secondary',
            }),
            rowStyle: (rowData) => (!getIsRowEnabled(rowData) ? { opacity: 0.5 } : {}),
        },
        onSelectionChange: (rows) => setSelectedDocuments(rows ?? []),
    };

    const isAllSelected =
        orderedDocumentTypes.length > 0 && selectedDocumentTypeIds.length === orderedDocumentTypes.length;

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            fullWidth
            maxWidth={isExpanded ? 'md' : 'sm'}
            PaperProps={{
                style: isExpanded
                    ? { borderRadius: 4 }
                    : { width: 530, maxWidth: 530, borderRadius: 4 },
            }}
        >
            {!isExpanded ? (
                <DialogTitle className={classes.title}>
                    Attach Documents
                    <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
            ) : (
                <IconButton aria-label="close" className={classes.closeButtonExpanded} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            )}

            <DialogContent
                className={isExpanded ? classes.expandedDialogContent : undefined}
                style={isExpanded ? undefined : { padding: '0 32px 16px 32px' }}
            >
                {!isExpanded ? (
                    <Typography variant="body1" className={classes.initialDescription}>
                        Would you like to attach documents to this referral before submitting?
                    </Typography>
                ) : (
                    <>
                        <div className={classes.documentsSectionHeader}>
                            <Typography variant="subtitle1" className={classes.documentsSectionTitle}>
                                Documents
                            </Typography>
                        </div>

                        <div className={classes.filtersContainer}>
                            <FormControlLabel
                                className={`${classes.filterLabel} ${classes.selectAllLabel}`}
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        disabled={props.loading || orderedDocumentTypes.length === 0}
                                    />
                                }
                                label="Select All"
                            />

                            {orderedDocumentTypes.map((documentType) => (
                                <FormControlLabel
                                    key={documentType.documentTypeId}
                                    className={`${classes.filterLabel} ${classes.documentTypeLabel}`}
                                    control={
                                        <Checkbox
                                            color="secondary"
                                            checked={selectedDocumentTypeIds.includes(documentType.documentTypeId)}
                                            onChange={(_event, checked) => handleSelectDocumentType(documentType.documentTypeId, checked)}
                                            disabled={props.loading}
                                        />
                                    }
                                    label={documentType.documentTypeName}
                                />
                            ))}
                        </div>

                        <Typography variant="subtitle2" className={classes.tableTitle}>
                            Available Documents
                        </Typography>

                        <div className={classes.tableContainer}>
                            {props.loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <MaterialTable
                                    components={{
                                        Container: TableContainer,
                                    }}
                                    {...tableProps}
                                />
                            )}
                        </div>
                    </>
                )}
            </DialogContent>

            <DialogActions style={{ padding: '0 16px 12px 16px', marginTop: 8 }}>
                {!isExpanded ? (
                    <>
                        <Button
                            onClick={handleNoSubmit}
                            variant="outlined"
                            className={classes.secondaryButton}
                            disabled={props.loading}
                        >
                            Skip & Submit
                        </Button>
                        <Button
                            onClick={handleYes}
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            disabled={props.loading}
                        >
                            Attach Documents
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={handleBack}
                            variant="outlined"
                            className={classes.secondaryButton}
                            disabled={props.loading}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={() => props.onSubmitWithDocuments(selectedDocuments)}
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            disabled={props.loading || selectedDocuments.length === 0}
                        >
                            Submit with Documents
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
